import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { Flex, Heading, Paragraph, Popover, Text } from '@buffer-mono/popcorn'

import facebookReactionsImage from './assets/facebook-reactions.png'

import styles from './FacebookReactionsHoverCard.module.css'

// Sustained hover before the card appears, mirroring Facebook's native
// hover-to-react tray rather than a quick tooltip.
const HOVER_OPEN_DELAY_MS = 850
// Grace period so moving the pointer from the icon onto the card (or briefly
// off it) doesn't flicker the card closed.
const HOVER_CLOSE_DELAY_MS = 150

type FacebookReactionsHoverCardProps = {
  /**
   * Prevents the card from opening while another overlay owns the like icon
   * (e.g. the first-time like coachmark), so the two never show at once.
   */
  suppressed?: boolean
  /** The like control the card points at. */
  children: ReactNode
}

/**
 * Educational hover card for Facebook comment likes. Buffer can only push a
 * "like", so this explains that other reactions are done on Facebook itself.
 * Shown on sustained hover of the like icon and hidden on leave — it mirrors
 * Facebook's native hover-to-react affordance, so it reappears each hover
 * rather than being a one-time onboarding hint. Keyboard focus opens it
 * immediately, since the icon's own tooltip is suppressed on Facebook and this
 * is the only place the sync caveat appears.
 *
 * Built on `Popover` with explicit hover timers (not `HoverCard`) so it matches
 * the LinkedIn reaction picker: pressing the like closes it synchronously and
 * it never competes with the first-time like coachmark — itself a `HoverCard` —
 * for the same anchor. Two hover cards on one anchor keep that coachmark from
 * opening until a second interaction.
 */
export const FacebookReactionsHoverCard = ({
  suppressed = false,
  children,
}: FacebookReactionsHoverCardProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set while a pointer press is focusing the like, to tell that focus apart
  // from a keyboard tab landing on it.
  const pointerPressRef = useRef(false)

  const clearTimers = useCallback((): void => {
    if (openTimer.current) clearTimeout(openTimer.current)
    if (closeTimer.current) clearTimeout(closeTimer.current)
    openTimer.current = null
    closeTimer.current = null
  }, [])

  const scheduleOpen = useCallback((): void => {
    if (suppressed) return
    clearTimers()
    openTimer.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS)
  }, [suppressed, clearTimers])

  const scheduleClose = useCallback((): void => {
    clearTimers()
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS)
  }, [clearTimers])

  const closeNow = useCallback((): void => {
    clearTimers()
    setOpen(false)
  }, [clearTimers])

  // A pointer press focuses the like as well, and that path must leave the card
  // closed so the coachmark can take the anchor. Only focus arriving on its own
  // — from the keyboard — opens the card.
  const handleFocus = useCallback((): void => {
    if (pointerPressRef.current || suppressed) return
    clearTimers()
    setOpen(true)
  }, [suppressed, clearTimers])

  const handleBlur = useCallback((): void => {
    pointerPressRef.current = false
    closeNow()
  }, [closeNow])

  const handlePointerDown = useCallback((): void => {
    pointerPressRef.current = true
    closeNow()
  }, [closeNow])

  // Suppression hides the card, so drop the open state with it. Leaving it set
  // would pop the card back up unprompted the moment suppression lifts.
  useEffect(() => {
    if (suppressed) closeNow()
  }, [suppressed, closeNow])

  return (
    <Popover
      open={open && !suppressed}
      onOpenChange={(next): void => {
        if (!next) closeNow()
      }}
    >
      <Popover.Anchor>
        <span
          className={styles.trigger}
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          onFocus={handleFocus}
          onBlur={handleBlur}
          // Close the moment the like is pressed, before the click's like
          // fires, so the first-time like coachmark can open on the first click.
          onPointerDown={handlePointerDown}
        >
          {children}
        </span>
      </Popover.Anchor>
      <Popover.Content
        side="top"
        align="center"
        arrow
        className={styles.content}
        onOpenAutoFocus={(e): void => e.preventDefault()}
        onMouseEnter={clearTimers}
        onMouseLeave={scheduleClose}
        onClick={(e): void => e.stopPropagation()}
      >
        <Flex direction="column" gap="sm">
          <div className={styles.illustration}>
            <img src={facebookReactionsImage} alt="" className={styles.image} />
          </div>
          <Flex direction="column" gap="2xs">
            <Heading size="sm">Likes for Facebook</Heading>
            <Paragraph>
              You can like comments via Buffer and add other reactions directly
              on Facebook.
            </Paragraph>
          </Flex>
          <Text size="sm" color="subtle">
            Buffer syncs reactions to Facebook, but reactions added there won't
            show here.
          </Text>
        </Flex>
      </Popover.Content>
    </Popover>
  )
}
